import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import {
  MapPinIcon,
  PhoneIcon,
  ClockIcon,
  ShareIcon,
  ArrowLeftIcon,
  CheckBadgeIcon,
  PhotoIcon,
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarSolidIcon } from '@heroicons/react/24/solid';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Helper to get full image URL
const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
  const staticBaseUrl = baseUrl.replace('/api', '');
  return `${staticBaseUrl}${url}`;
};

const RestaurantDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [restaurant, setRestaurant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPhoto, setSelectedPhoto] = useState(0);
  const [showGallery, setShowGallery] = useState(false);
  const [showMenuGallery, setShowMenuGallery] = useState(false);
  const [selectedMenuPhoto, setSelectedMenuPhoto] = useState(0);

  useEffect(() => {
    fetchRestaurant();
  }, [id]);

  const fetchRestaurant = async () => {
    try {
      const response = await axios.get(`${API_URL}/restaurants/${id}`);
      setRestaurant(response.data);
    } catch (err) {
      setError('Restaurant not found');
      console.error('Error fetching restaurant:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: restaurant.name,
          text: `Check out ${restaurant.name} on LOCU - Get ${restaurant.discountPercentage || 20}% off!`,
          url: window.location.href,
        });
      } catch (err) {
        console.log('Share cancelled');
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  const handleCall = () => {
    if (restaurant.phone) {
      window.location.href = `tel:${restaurant.phone}`;
    }
  };

  const handleDirections = () => {
    if (restaurant.googleMapsUrl) {
      window.open(restaurant.googleMapsUrl, '_blank');
    } else if (restaurant.location?.latitude && restaurant.location?.longitude) {
      window.open(`https://www.google.com/maps/dir/?api=1&destination=${restaurant.location.latitude},${restaurant.location.longitude}`, '_blank');
    } else if (restaurant.address) {
      window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(restaurant.address)}`, '_blank');
    }
  };

  const handleRedeem = () => {
    navigate('/redemption', { state: { restaurant } });
  };

  const nextPhoto = () => {
    const photos = restaurant?.photos || [];
    if (photos.length > 0) {
      setSelectedPhoto((prev) => (prev + 1) % photos.length);
    }
  };

  const prevPhoto = () => {
    const photos = restaurant?.photos || [];
    if (photos.length > 0) {
      setSelectedPhoto((prev) => (prev - 1 + photos.length) % photos.length);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error || !restaurant) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
        <p className="text-gray-600 mb-4">{error || 'Restaurant not found'}</p>
        <button
          onClick={() => navigate('/neighborhood')}
          className="text-primary-600 hover:text-primary-700 font-medium"
        >
          Back to Neighborhood
        </button>
      </div>
    );
  }

  const allPhotos = restaurant.photos || [];
  const foodPhotos = allPhotos.filter(p => p.category === 'food');
  const menuPhotos = restaurant.menuImages || [];
  const discountPercentage = restaurant.discountPercentage || 20;
  const userPoints = user?.currentRewardBalance || 0;
  const openingHours = restaurant.openingHours || { open: '12:00 PM', close: '11:00 PM' };

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {/* Photo Gallery Modal */}
      {showGallery && allPhotos.length > 0 && (
        <div className="fixed inset-0 bg-black z-50 flex flex-col">
          <div className="flex items-center justify-between p-4">
            <button onClick={() => setShowGallery(false)} className="text-white">
              <XMarkIcon className="h-8 w-8" />
            </button>
            <span className="text-white">{selectedPhoto + 1} / {allPhotos.length}</span>
            <div className="w-8" />
          </div>
          <div className="flex-1 flex items-center justify-center p-4 relative">
            <img
              src={getImageUrl(allPhotos[selectedPhoto]?.url)}
              alt=""
              className="max-h-full max-w-full object-contain"
            />
            {allPhotos.length > 1 && (
              <>
                <button
                  onClick={prevPhoto}
                  className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/30 text-white p-3 rounded-full transition"
                >
                  <ChevronLeftIcon className="h-6 w-6" />
                </button>
                <button
                  onClick={nextPhoto}
                  className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/30 text-white p-3 rounded-full transition"
                >
                  <ChevronRightIcon className="h-6 w-6" />
                </button>
              </>
            )}
          </div>
          <div className="flex gap-2 p-4 overflow-x-auto justify-center">
            {allPhotos.map((photo, idx) => (
              <button
                key={idx}
                onClick={() => setSelectedPhoto(idx)}
                className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 ${idx === selectedPhoto ? 'border-primary-500' : 'border-transparent'}`}
              >
                <img src={getImageUrl(photo.url)} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Menu Gallery Modal */}
      {showMenuGallery && menuPhotos.length > 0 && (
        <div className="fixed inset-0 bg-black z-50 flex flex-col">
          <div className="flex items-center justify-between p-4">
            <button onClick={() => setShowMenuGallery(false)} className="text-white">
              <XMarkIcon className="h-8 w-8" />
            </button>
            <span className="text-white">Menu {selectedMenuPhoto + 1} / {menuPhotos.length}</span>
            <div className="w-8" />
          </div>
          <div className="flex-1 flex items-center justify-center p-4 overflow-auto">
            <img
              src={getImageUrl(menuPhotos[selectedMenuPhoto]?.url)}
              alt=""
              className="max-w-full object-contain"
            />
          </div>
          {menuPhotos.length > 1 && (
            <div className="flex gap-2 p-4 overflow-x-auto justify-center">
              {menuPhotos.map((photo, idx) => (
                <button
                  key={idx}
                  onClick={() => setSelectedMenuPhoto(idx)}
                  className={`flex-shrink-0 w-16 h-20 rounded-lg overflow-hidden border-2 ${idx === selectedMenuPhoto ? 'border-primary-500' : 'border-transparent'}`}
                >
                  <img src={getImageUrl(photo.url)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Header with back button */}
      <div className="sticky top-0 bg-white/95 backdrop-blur-sm z-40 border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-gray-600 hover:text-gray-900"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Back
          </button>
          <button
            onClick={handleShare}
            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full"
          >
            <ShareIcon className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Photo Gallery - Zomato style grid */}
      <div className="max-w-4xl mx-auto">
        {allPhotos.length > 0 ? (
          <div className="relative">
            <div className="grid grid-cols-4 gap-1 h-64 md:h-80">
              {/* Main photo - spans 2 cols and 2 rows */}
              <button
                onClick={() => { setSelectedPhoto(0); setShowGallery(true); }}
                className="col-span-2 row-span-2 relative overflow-hidden"
              >
                <img
                  src={getImageUrl(allPhotos[0]?.url)}
                  alt={restaurant.name}
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                />
              </button>
              {/* Secondary photos */}
              {[1, 2, 3, 4].map((idx) => (
                <button
                  key={idx}
                  onClick={() => { setSelectedPhoto(Math.min(idx, allPhotos.length - 1)); setShowGallery(true); }}
                  className="relative overflow-hidden bg-gray-200"
                >
                  {allPhotos[idx] ? (
                    <img
                      src={getImageUrl(allPhotos[idx]?.url)}
                      alt=""
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400">
                      <PhotoIcon className="h-8 w-8" />
                    </div>
                  )}
                  {idx === 4 && allPhotos.length > 5 && (
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                      <span className="text-white font-bold text-lg">+{allPhotos.length - 5}</span>
                    </div>
                  )}
                </button>
              ))}
            </div>
            {allPhotos.length > 1 && (
              <button
                onClick={() => { setSelectedPhoto(0); setShowGallery(true); }}
                className="absolute bottom-4 right-4 bg-white/90 hover:bg-white text-gray-900 px-4 py-2 rounded-lg font-medium text-sm flex items-center gap-2 shadow-lg"
              >
                <PhotoIcon className="h-4 w-4" />
                View all ({allPhotos.length})
              </button>
            )}
          </div>
        ) : (
          <div className="h-64 bg-gradient-to-br from-primary-100 to-purple-100 flex items-center justify-center">
            <PhotoIcon className="h-16 w-16 text-primary-300" />
          </div>
        )}
      </div>

      {/* Restaurant Info */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Discount Badge */}
        <div className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-semibold mb-3">
          <CheckBadgeIcon className="h-4 w-4" />
          {discountPercentage}% OFF with LOCU
        </div>

        {/* Name and Rating */}
        <div className="flex items-start justify-between mb-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-1">{restaurant.name}</h1>
            <p className="text-gray-600">{restaurant.cuisine}</p>
          </div>
          {restaurant.rating > 0 && (
            <div className="flex items-center gap-1 px-3 py-1 bg-green-500 text-white rounded-lg">
              <span className="font-bold">{restaurant.rating.toFixed(1)}</span>
              <StarSolidIcon className="h-4 w-4" />
            </div>
          )}
        </div>

        {/* Quick Info */}
        <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 mb-6">
          {restaurant.priceForTwo > 0 && (
            <div className="flex items-center gap-1">
              <span>₹{restaurant.priceForTwo} for two</span>
            </div>
          )}
          {restaurant.openingHours && (
            <div className="flex items-center gap-1 text-green-600">
              <ClockIcon className="h-4 w-4" />
              <span>Open • {openingHours.open} - {openingHours.close}</span>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 mb-8">
          <button
            onClick={handleDirections}
            className="flex-1 flex items-center justify-center gap-2 py-3 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors shadow-sm"
          >
            <MapPinIcon className="h-5 w-5 text-primary-600" />
            <span className="font-medium">Directions</span>
          </button>
          {restaurant.phone && (
            <button
              onClick={handleCall}
              className="flex-1 flex items-center justify-center gap-2 py-3 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors shadow-sm"
            >
              <PhoneIcon className="h-5 w-5 text-primary-600" />
              <span className="font-medium">Call</span>
            </button>
          )}
          <button
            onClick={handleShare}
            className="flex-1 flex items-center justify-center gap-2 py-3 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors shadow-sm"
          >
            <ShareIcon className="h-5 w-5 text-primary-600" />
            <span className="font-medium">Share</span>
          </button>
        </div>

        {/* Address Card */}
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 mb-6">
          <h3 className="font-semibold text-gray-900 mb-2">Location</h3>
          <p className="text-gray-600 mb-3">{restaurant.address}</p>
          <button
            onClick={handleDirections}
            className="text-primary-600 font-medium text-sm hover:text-primary-700"
          >
            Get Directions →
          </button>
        </div>

        {/* Description */}
        {restaurant.description && (
          <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 mb-6">
            <h3 className="font-semibold text-gray-900 mb-2">About</h3>
            <p className="text-gray-600 leading-relaxed">{restaurant.description}</p>
          </div>
        )}

        {/* Must Try Dishes */}
        {restaurant.mustTryDishes?.length > 0 && (
          <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 mb-6">
            <h3 className="font-semibold text-gray-900 mb-3">Must Try</h3>
            <div className="flex flex-wrap gap-2">
              {restaurant.mustTryDishes.map((dish, idx) => (
                <span key={idx} className="px-3 py-1.5 bg-orange-100 text-orange-700 rounded-full text-sm font-medium">
                  {dish}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Facilities */}
        {restaurant.facilities?.length > 0 && (
          <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 mb-6">
            <h3 className="font-semibold text-gray-900 mb-3">Facilities</h3>
            <div className="flex flex-wrap gap-2">
              {restaurant.facilities.map((facility, idx) => (
                <span key={idx} className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-full text-sm">
                  {facility}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Menu Section */}
        {menuPhotos.length > 0 && (
          <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 mb-6">
            <h3 className="font-semibold text-gray-900 mb-3">Menu</h3>
            <div className="grid grid-cols-3 gap-2">
              {menuPhotos.slice(0, 3).map((photo, idx) => (
                <button
                  key={idx}
                  onClick={() => { setSelectedMenuPhoto(idx); setShowMenuGallery(true); }}
                  className="aspect-[3/4] rounded-lg overflow-hidden relative bg-gray-100"
                >
                  <img
                    src={getImageUrl(photo.url)}
                    alt={`Menu page ${idx + 1}`}
                    className="w-full h-full object-cover"
                  />
                  {idx === 2 && menuPhotos.length > 3 && (
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                      <span className="text-white font-bold">+{menuPhotos.length - 3}</span>
                    </div>
                  )}
                </button>
              ))}
            </div>
            <button
              onClick={() => { setSelectedMenuPhoto(0); setShowMenuGallery(true); }}
              className="w-full mt-3 py-2 text-primary-600 font-medium text-sm hover:text-primary-700"
            >
              View Full Menu →
            </button>
          </div>
        )}

        {/* External Links - Order Online */}
        {(restaurant.zomatoUrl || restaurant.swiggyUrl) && (
          <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 mb-6">
            <h3 className="font-semibold text-gray-900 mb-3">Order Online</h3>
            <div className="flex gap-3">
              {restaurant.zomatoUrl && (
                <a
                  href={restaurant.zomatoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 py-3 bg-red-500 text-white rounded-xl text-center font-medium hover:bg-red-600 transition-colors"
                >
                  Zomato
                </a>
              )}
              {restaurant.swiggyUrl && (
                <a
                  href={restaurant.swiggyUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 py-3 bg-orange-500 text-white rounded-xl text-center font-medium hover:bg-orange-600 transition-colors"
                >
                  Swiggy
                </a>
              )}
            </div>
          </div>
        )}

        {/* Food Photos */}
        {foodPhotos.length > 0 && (
          <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 mb-6">
            <h3 className="font-semibold text-gray-900 mb-3">Food Photos</h3>
            <div className="grid grid-cols-3 gap-2">
              {foodPhotos.slice(0, 6).map((photo, idx) => (
                <button
                  key={idx}
                  onClick={() => {
                    const photoIndex = allPhotos.findIndex(p => p.url === photo.url);
                    setSelectedPhoto(photoIndex >= 0 ? photoIndex : 0);
                    setShowGallery(true);
                  }}
                  className="aspect-square rounded-lg overflow-hidden"
                >
                  <img
                    src={getImageUrl(photo.url)}
                    alt=""
                    className="w-full h-full object-cover hover:scale-105 transition-transform"
                  />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Sticky Redeem Button */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 z-30 shadow-lg">
        <div className="max-w-4xl mx-auto flex items-center justify-between gap-4">
          <div>
            <p className="text-sm text-gray-500">Your balance</p>
            <p className="text-lg font-bold text-primary-600">{userPoints.toLocaleString('en-IN')} points</p>
          </div>
          <button
            onClick={handleRedeem}
            className="flex-1 max-w-xs py-3.5 bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-xl font-semibold hover:from-primary-600 hover:to-primary-700 transition-all shadow-lg"
          >
            Redeem {discountPercentage}% Off
          </button>
        </div>
      </div>
    </div>
  );
};

export default RestaurantDetail;
